import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { KPILocationValue } from '../constants';
import { KpiUpdatesDialog } from '../remote-modules';
import { getSharedData } from '../utils';

const OPEN_KPI_UPDATES_DIALOG_EVENT = 'kpi-updates-dialog:open';

interface OpenKpiUpdatesPayload {
  id: number;
  location: KPILocationValue;
  onSubmit: () => void;
  onClose?: () => void;
}

type OpenEvent = CustomEvent<OpenKpiUpdatesPayload>;

export function openKpiUpdatesDialog(payload: OpenKpiUpdatesPayload) {
  const event = new CustomEvent<OpenKpiUpdatesPayload>(
    OPEN_KPI_UPDATES_DIALOG_EVENT,
    { detail: payload },
  );

  window.dispatchEvent(event);
}

export function useOpenKpiUpdatesDialog() {
  const open = useCallback((payload: OpenKpiUpdatesPayload) => {
    openKpiUpdatesDialog(payload);
  }, []);

  return open;
}

export function KpiUpdatesDialogProvider({ children }: React.PropsWithChildren) {
  const [dialogProps, setDialogProps] = useState<OpenKpiUpdatesPayload | null>(
    null,
  );

  const sharedData = useMemo(
    () => getSharedData(dialogProps?.location),
    [dialogProps?.location],
  );

  const isOpen = !!dialogProps;

  useEffect(() => {
    const onOpen = (event: Event) => {
      const customEvent = event as OpenEvent;

      setDialogProps(customEvent.detail);
    };

    window.addEventListener(
      OPEN_KPI_UPDATES_DIALOG_EVENT,
      onOpen as EventListener,
    );

    // Cleanup the event listener
    return () =>
      window.removeEventListener(
        OPEN_KPI_UPDATES_DIALOG_EVENT,
        onOpen as EventListener,
      );
  }, []);

  const handleClose = () => {
    dialogProps?.onClose?.();

    setDialogProps(null);
  };

  const handleSubmit = () => {
    dialogProps?.onSubmit?.();

    setDialogProps(null);
  };

  return (
    <>
      {children}
      {isOpen && (
        <KpiUpdatesDialog
          id={dialogProps?.id ?? 0}
          onSubmit={handleSubmit}
          onClose={handleClose}
          {...sharedData}
        />
      )}
    </>
  );
}
